import Drawer from '@mui/material/Drawer';
import {
    createContext,
    useCallback,
    useContext,
    useState,
    type ReactNode,
} from 'react';

const DRAWER_WIDTH = 520;

export type DrawerState = {
    open: boolean;
    onClose: () => void;
};

export type DrawerContextValue = {
    openDrawer: (renderDrawer: (state: DrawerState) => ReactNode) => void;
    closeDrawer: () => void;
};

const DrawerContext = createContext<DrawerContextValue>({
    openDrawer: () => {},
    closeDrawer: () => {},
});

export function useDrawer(): DrawerContextValue {
    return useContext(DrawerContext);
}

type DrawerProviderProps = {
    children: ReactNode;
};

export function DrawerProvider({ children }: DrawerProviderProps) {
    const [open, setOpen] = useState(false);
    const [renderDrawerContent, setRenderDrawerContent] = useState<
        ((state: DrawerState) => ReactNode) | null
    >(null);

    const onClose = useCallback(() => {
        setOpen(false);
    }, []);

    const openDrawer = useCallback((fn: (state: DrawerState) => ReactNode) => {
        setOpen(true);
        setRenderDrawerContent(() => fn);
    }, []);

    const closeDrawer = useCallback(() => {
        setOpen(false);
    }, []);

    return (
        <DrawerContext.Provider value={{ openDrawer, closeDrawer }}>
            {children}

            <Drawer
                anchor="right"
                open={open}
                onClose={onClose}
                PaperProps={{
                    sx: {
                        width: { xs: '100%', sm: DRAWER_WIDTH },
                    },
                }}
            >
                {renderDrawerContent && renderDrawerContent({ open, onClose })}
            </Drawer>
        </DrawerContext.Provider>
    );
}
